import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  InputLabel,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import moment, { Moment } from 'moment';
import axios from 'axios';
import { hapticsImpactLight } from '../../../utils/haptics';
import CustomModal from '../../Global/CustomModal';
import {
  Lecture,
  SparseFormattedFlashcard,
  SparseFormattedVignette,
} from '../../../types/Lecture';
import ThreeOrb from '../../Home/ThreeOrb';
import { ArrowRight, Info, Trash2 } from 'react-feather';
import { getLectureUploadUrl } from '../../../services/aws';
import { File } from '@phosphor-icons/react';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { useSnackbar } from 'notistack';
import parse from 'html-react-parser';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { UserRole } from '../../../types/User';
import { RootState } from '../../../store/store';
import useWindowWidth from '../../../hooks/useWindowWidth';
import { Capacitor } from '@capacitor/core';

interface LectureEditorModalProps {
  readOnlyMode?: boolean;
  handleClose: () => void;
  handleLectureSave: (lecture: Lecture) => void;
  editLecture?: Lecture;
  handleMapFile: (
    s3Key: string,
    originalFilename: string,
    existingLectureId?: string
  ) => void;
}

const ACCEPT_FILE_TYPES =
  '.pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation';

const LectureEditorModal: React.FC<LectureEditorModalProps> = ({
  readOnlyMode = false,
  handleClose,
  handleLectureSave,
  editLecture,
  handleMapFile,
}) => {
  const isEditMode = Boolean(editLecture);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [lectureName, setLectureName] = useState<string>(
    editLecture?.name || ''
  );
  const [lectureDate, setLectureDate] = useState<Moment | null>(
    editLecture?.date ? moment(editLecture.date) : null
  );
  const [lectureDescription, setLectureDescription] = useState<string>('');
  const [lectureDescriptionMode, setLectureDescriptionMode] = useState<
    'manual' | 'upload'
  >('upload');

  const [isMapping, setIsMapping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [mobileTab, setMobileTab] = useState<'qbank' | 'flashcards'>('qbank');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [loadedVignettes, setLoadedVignettes] = useState<
    SparseFormattedVignette[]
  >(editLecture?.vignettes || []);
  const [loadedFlashcards, setLoadedFlashcards] = useState<
    SparseFormattedFlashcard[]
  >(editLecture?.flashcards || []);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [s3Key, setS3Key] = useState<string | null>(editLecture?.s3Key || null);

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { enqueueSnackbar } = useSnackbar();
  const windowWidth = useWindowWidth();

  const canMap = Boolean(!isMapping && lectureDescription.trim().length > 0);

  const isMapped = Boolean(
    loadedFlashcards?.length > 0 || loadedVignettes?.length > 0
  );

  const canSubmit = Boolean(
    hasUnsavedChanges &&
      lectureName.trim().length > 0 &&
      isMapped &&
      primaryExam?.type === 'CUSTOM' &&
      !readOnlyMode
  );

  const managerPermissions = Boolean(
    (currentUser?.role === UserRole.admin || currentUser?.ambassador) &&
      !currentUser?.demoMode &&
      !readOnlyMode
  );

  // Check if the lecture has unsaved changes
  useEffect(() => {
    if (isEditMode) {
      const hasNameChanged = lectureName !== (editLecture?.name || '');
      const hasDateChanged = !moment(lectureDate).isSame(editLecture?.date);
      const hasVignettesChanged = !_.isEqual(
        new Set(loadedVignettes.map((v) => v.id)),
        new Set(editLecture?.vignettes?.map((v) => v.id) || [])
      );
      const hasFlashcardsChanged = !_.isEqual(
        new Set(loadedFlashcards.map((f) => f.id)),
        new Set(editLecture?.flashcards?.map((f) => f.id) || [])
      );

      if (
        hasNameChanged ||
        hasDateChanged ||
        hasVignettesChanged ||
        hasFlashcardsChanged
      ) {
        setHasUnsavedChanges(true);
      } else {
        setHasUnsavedChanges(false);
      }
    } else {
      if (lectureDescription.trim().length > 0) {
        setHasUnsavedChanges(true);
      } else {
        setHasUnsavedChanges(false);
      }
    }
  }, [
    isEditMode,
    lectureName,
    lectureDate,
    lectureDescription,
    editLecture,
    loadedVignettes,
    loadedFlashcards,
  ]);

  useEffect(() => {
    if (isMapped && !readOnlyMode && _.isNull(lectureDate)) {
      const dateInput = document.getElementById('lecture-date-input');
      if (dateInput) {
        dateInput.focus();
      }
    }
  }, [isMapped, readOnlyMode, lectureDate]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    []
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
    },
    []
  );

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    handleUploadAndMapFile(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    handleUploadAndMapFile(file);
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleUploadAndMapFile = async (file?: File | null) => {
    if (readOnlyMode) {
      console.error('Cannot upload document in read-only mode');
      return;
    }
    const s3Key = await uploadFile(file);
    if (s3Key) {
      setS3Key(s3Key);
      handleMapFile(s3Key, file?.name || 'Pending Lecture', editLecture?.id);
    }
    handleClose();
  };

  const uploadFile = async (file?: File | null) => {
    if (!file) {
      console.error('No file was uploaded');
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
    ];

    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Please upload a .pdf, .pptx, or .txt file.', {
        autoHideDuration: 3000,
      });
      return;
    }
    setIsUploading(true);

    let uploadDetails;
    try {
      const fileInputField = document.getElementById('topic-input');
      if (fileInputField) {
        fileInputField.blur();
      }

      uploadDetails = await getLectureUploadUrl({
        originalFilename: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      if (!uploadDetails.uploadUrl || !uploadDetails.key) {
        throw new Error('Received invalid upload details from the server.');
      }
      if (file.size > uploadDetails.max_size_bytes) {
        throw new Error(
          `File size exceeds the maximum allowed size of ${uploadDetails.max_size_mb}MB.`
        );
      }
    } catch (error) {
      console.error('Failed to get lecture upload URL:', error);
      setIsUploading(false);
      return;
    }

    let s3Key: string;
    try {
      await axios.put(uploadDetails.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
      });

      s3Key = uploadDetails.key;
      setIsUploading(false);
    } catch (error) {
      console.error('Failed to upload file directly to S3:', error);
      setIsUploading(false);
      return;
    }
    setS3Key(s3Key);
    return s3Key;
  };

  const handleMapDescription = async () => {
    if (readOnlyMode) {
      console.error('Cannot map lecture description in read-only mode');
      return;
    }

    if (!canMap) return;
    hapticsImpactLight();
    setIsMapping(true);

    // Convert description to a file and upload
    const textFile = new window.File(
      [lectureDescription],
      'lecture_content.txt',
      {
        type: 'text/plain',
      }
    );

    handleUploadAndMapFile(textFile);
  };

  const handleSubmit = useCallback(async () => {
    if (readOnlyMode) {
      console.error('Cannot submit lecture in read-only mode');
      return;
    }

    if (!canSubmit) return;

    const lectureData: Lecture = {
      id: editLecture?.id || `new_${Date.now()}`,
      name: lectureName.trim(),
      date: lectureDate || moment.utc(),
      vignettes: loadedVignettes,
      flashcards: loadedFlashcards,
      s3Key: s3Key || undefined,
    };

    handleLectureSave(lectureData);
    hapticsImpactLight();
    handleClose();
  }, [
    lectureName,
    lectureDate,
    loadedVignettes,
    loadedFlashcards,
    editLecture,
    handleLectureSave,
    handleClose,
    canSubmit,
  ]);

  const handleCancel = () => {
    hapticsImpactLight();
    handleClose();
  };

  const handleDateChange = (newDate: Moment | null) => {
    if (readOnlyMode) {
      console.error('Cannot change lecture date in read-only mode');
      return;
    }
    setLectureDate(newDate);
  };

  const handleClearDate = () => {
    if (readOnlyMode) {
      console.error('Cannot clear lecture date in read-only mode');
      return;
    }
    setLectureDate(null);
  };

  const handleRemoveVignette = (vignetteId: string) => {
    if (readOnlyMode) {
      console.error('Cannot remove vignette in read-only mode');
      return;
    }
    hapticsImpactLight();
    setLoadedVignettes((prevVignettes) =>
      prevVignettes.filter((v) => v.id !== vignetteId)
    );
    enqueueSnackbar('QBank question removed.', {
      autoHideDuration: 3000,
    });
  };

  const handleRemoveFlashcard = (flashcardId: string) => {
    if (readOnlyMode) {
      console.error('Cannot remove flashcard in read-only mode');
      return;
    }
    hapticsImpactLight();
    setLoadedFlashcards((prevFlashcards) =>
      prevFlashcards.filter((f) => f.id !== flashcardId)
    );
    enqueueSnackbar('Flashcard removed.', {
      autoHideDuration: 3000,
    });
  };

  const handleUnsavedChanges = () => {
    if (cancelButtonRef.current) {
      cancelButtonRef.current.classList.add('shake');
    }
    if (submitButtonRef.current) {
      submitButtonRef.current.classList.add('shake');
    }
    setTimeout(() => {
      if (cancelButtonRef.current) {
        cancelButtonRef.current.classList.remove('shake');
      }
      if (submitButtonRef.current) {
        submitButtonRef.current.classList.remove('shake');
      }
    }, 500);
  };

  const handleClearContent = () => {
    if (readOnlyMode) {
      console.error('Cannot clear mapped content in read-only mode');
      return;
    }
    setShowClearConfirmation(true);
  };

  const handleConfirmClear = () => {
    setLoadedVignettes([]);
    setLoadedFlashcards([]);
    setShowClearConfirmation(false);
    hapticsImpactLight();
  };

  const rerunMapping = () => {
    if (readOnlyMode) {
      console.error('Cannot rerun mapping in read-only mode');
      return;
    }
    if (!managerPermissions) {
      console.error('Cannot rerun mapping in non-admin mode');
      return;
    }
    if (!s3Key) {
      console.error('No S3 key found');
      return;
    }
    try {
      handleMapFile(
        s3Key,
        editLecture?.name || 'Pending Lecture',
        editLecture?.id
      );
      handleClose();
    } catch (error) {
      console.error('Failed to map topics from file via backend:', error);
    }
  };

  return (
    <CustomModal
      open={true}
      onClose={handleClose}
      name={'add-lecture'}
      glass={true}
      unsavedChanges={hasUnsavedChanges}
      handleUnsavedChanges={handleUnsavedChanges}
    >
      <div className="modal_header">
        <h2>
          {readOnlyMode
            ? lectureName
            : isEditMode
              ? 'Edit Lecture'
              : 'Add Lecture'}
        </h2>
        {isMapped && !readOnlyMode && (
          <button
            type="button"
            className="button"
            onClick={handleClearContent}
            tabIndex={0}
            aria-label="Clear Content and Restart"
          >
            Clear Content &amp; Restart
          </button>
        )}
      </div>
      <div className="modal_content">
        <form className="add-lecture-form" onSubmit={(e) => e.preventDefault()}>
          {isMapped && !readOnlyMode && (
            <div className="add-lecture-form_header">
              <div className="form-group">
                <InputLabel
                  id="lecture-name-label"
                  htmlFor="lecture-name-input"
                  className="m-b-0-50"
                >
                  Lecture Title
                </InputLabel>
                <TextField
                  id="lecture-name-input"
                  value={lectureName}
                  onChange={(e) => setLectureName(e.target.value)}
                  placeholder="e.g. Staph & Strep Infections"
                  fullWidth
                  required
                  aria-labelledby="lecture-name-label"
                  tabIndex={0}
                />
              </div>
              <div className="form-group">
                <InputLabel
                  id="lecture-date-label"
                  htmlFor="lecture-date-input"
                  className="m-b-0-50"
                >
                  Lecture Date
                </InputLabel>
                <DatePicker
                  value={lectureDate}
                  onChange={handleDateChange}
                  minDate={moment.utc().subtract(1, 'year').startOf('day')}
                  maxDate={
                    primaryExam?.examDate
                      ? moment(primaryExam.examDate).subtract(1, 'day')
                      : moment.utc().add(7, 'years')
                  }
                  openTo="month"
                  views={['year', 'month', 'day']}
                  slotProps={{
                    field: {
                      id: 'lecture-date-input',
                      clearable: false,
                      onClear: handleClearDate,
                    },
                  }}
                />
              </div>
            </div>
          )}
          <div
            className={`add-lecture-form_content ${isMapped ? 'show-mapped' : ''}`}
          >
            {!isMapped && (
              <div className="add-lecture-form_content_input">
                <InputLabel htmlFor="topic-input" className="m-b-0-50">
                  {lectureDescriptionMode === 'upload'
                    ? 'Lecture Slides or PDF'
                    : 'Lecture Content'}
                  <Tooltip
                    title={
                      'Ora AI analyzes your lecture and matches it to the most relevant content.'
                    }
                    enterTouchDelay={0}
                  >
                    <Info />
                  </Tooltip>
                </InputLabel>
                {lectureDescriptionMode === 'upload' && (
                  <DndContext sensors={sensors}>
                    <div
                      className={`upload-lecture ${isDragging ? 'upload-lecture--dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="upload-lecture_label">
                        <div className="upload-lecture_label_icon">
                          <File size={24} />
                        </div>
                        <div className="upload-lecture_label_text">
                          <div className="upload-lecture_label_text_main">
                            {Capacitor.isNativePlatform() || windowWidth <= 560
                              ? 'Tap to upload'
                              : 'Drag and drop or click to upload'}
                          </div>
                          <div className="upload-lecture_label_text_sub">
                            (PowerPoint or PDF)
                          </div>
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="topic-input"
                        accept={ACCEPT_FILE_TYPES}
                        onChange={handleFileChange}
                        disabled={isUploading}
                        style={{ display: 'block', marginBottom: '10px' }}
                      />
                      {isUploading && (
                        <div className="doc-upload-overlay">
                          <CircularProgress
                            sx={{
                              color: 'var(--type-body-dark)',
                              height: '1rem !important',
                              width: '1rem !important',
                            }}
                          />
                          Uploading Document...
                        </div>
                      )}
                    </div>
                  </DndContext>
                )}
                {lectureDescriptionMode === 'manual' && (
                  <div className="manual-add">
                    <textarea
                      id="topic-input"
                      value={lectureDescription}
                      onChange={(e) => setLectureDescription(e.target.value)}
                      rows={4}
                      placeholder={'Paste or type lecture content here...'}
                      aria-labelledby="lecture-description-label"
                      maxLength={100000}
                      tabIndex={0}
                    />
                    {canMap && (
                      <div className="map-button-container">
                        <button
                          type="button"
                          className="button"
                          onClick={handleMapDescription}
                          disabled={!canMap || isMapping}
                          tabIndex={0}
                          aria-label={isMapping ? 'Analyzing...' : 'Continue'}
                        >
                          {isMapping ? 'Analyzing...' : 'Continue'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div className="add-lecture-form_content_input_footer">
                  <div className="or">or</div>
                  <button
                    type="button"
                    className="button button--link button--change-mode"
                    onClick={() =>
                      setLectureDescriptionMode(
                        lectureDescriptionMode === 'upload'
                          ? 'manual'
                          : 'upload'
                      )
                    }
                    tabIndex={0}
                    aria-label={`Change to ${lectureDescriptionMode === 'upload' ? 'manual' : 'upload'} mode`}
                  >
                    {lectureDescriptionMode === 'upload'
                      ? 'Add Lecture Manually'
                      : 'Upload Lecture'}
                    <ArrowRight />
                  </button>
                </div>
              </div>
            )}
            {isMapped && (
              <>
                <div
                  className={`add-lecture-form_content_mapping ${mobileTab === 'qbank' ? 'qbank' : 'flashcards'}`}
                >
                  {isMapped ? (
                    <>
                      <div className="mobile-tabs">
                        <button
                          className={`button button--link ${mobileTab === 'qbank' ? 'active' : ''}`}
                          onClick={() => {
                            setMobileTab('qbank');
                            hapticsImpactLight();
                          }}
                        >
                          QBank
                          <span className="badge">
                            {loadedVignettes.length}
                          </span>
                        </button>
                        <button
                          className={`button button--link ${mobileTab === 'flashcards' ? 'active' : ''}`}
                          onClick={() => {
                            setMobileTab('flashcards');
                            hapticsImpactLight();
                          }}
                        >
                          Flashcards
                          <span className="badge">
                            {loadedFlashcards.length}
                          </span>
                        </button>
                      </div>
                      <div className="mapped-topics-list mapped-topics-list--vignettes">
                        <div className="mapped-topics-list_header">
                          <span>{loadedVignettes.length}</span> QBank
                        </div>
                        <div className="mapped-topics-list_items">
                          {loadedVignettes.map((vignette) => (
                            <div
                              className="mapped-topics-list_items_item"
                              key={vignette.id}
                            >
                              <div className="content-card content-card--vignette">
                                <div className="content-card_text">
                                  {parse(vignette.question)}
                                  <div className="content-card_answers">
                                    {vignette.answerOptions.map((option) => (
                                      <div
                                        key={option.optionLetter}
                                        className="answer-choice"
                                      >
                                        {parse(option.optionText)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              {!readOnlyMode && (
                                <button
                                  type="button"
                                  className="button button--icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveVignette(vignette.id);
                                  }}
                                  aria-label="Remove vignette"
                                  tabIndex={0}
                                >
                                  <Trash2 />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mapped-topics-list mapped-topics-list--flashcards">
                        <div className="mapped-topics-list_header">
                          <span>{loadedFlashcards.length}</span> Flashcards
                        </div>
                        <div className="mapped-topics-list_items">
                          {loadedFlashcards.map((flashcard) => (
                            <div
                              className="mapped-topics-list_items_item"
                              key={flashcard.id}
                            >
                              <div className="content-card">
                                <div
                                  className={`content-card_front ${flashcard.front.length > flashcard.back.length ? 'structural' : ''}`}
                                >
                                  {parse(flashcard.front)}
                                </div>
                                <div
                                  className={`content-card_back ${flashcard.front.length > flashcard.back.length ? 'front-based' : ''}`}
                                >
                                  {parse(flashcard.back)}
                                </div>
                              </div>
                              {!readOnlyMode && (
                                <button
                                  type="button"
                                  className="button button--icon button--transparent"
                                  onClick={() =>
                                    handleRemoveFlashcard(flashcard.id)
                                  }
                                  aria-label={`Remove flashcard: ${flashcard.front}`}
                                >
                                  <Trash2 />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="mapped-topics-placeholder">
                      No assigned topics yet. Add a description and click
                      "Confirm".
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {managerPermissions && s3Key && (
            <div className="add-lecture-form_admin-controls">
              <div className="admin-controls-container">
                <div className="admin-controls-container_label">S3 Key</div>
                <div className="admin-controls-container_key">
                  {s3Key || 'No S3 key found'}
                </div>
                {s3Key && (
                  <button
                    type="button"
                    className="button"
                    onClick={rerunMapping}
                    disabled={isMapping}
                    tabIndex={0}
                    aria-label="Rerun Mapping"
                  >
                    Rerun Mapping
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
      <div className="modal_actions">
        <button
          ref={cancelButtonRef}
          type="button"
          className="button button--secondary"
          onClick={handleCancel}
          style={readOnlyMode ? { marginRight: 'auto', marginLeft: '0' } : {}}
          tabIndex={0}
          aria-label={readOnlyMode ? 'Close' : 'Cancel'}
        >
          {readOnlyMode ? 'Close' : 'Cancel'}
        </button>

        {!readOnlyMode && (
          <Tooltip
            title={
              !canSubmit && !lectureDate && !isEditMode
                ? 'Add a lecture date to continue.'
                : ''
            }
            enterTouchDelay={0}
          >
            <span>
              <button
                ref={submitButtonRef}
                type="button"
                className="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                tabIndex={0}
                aria-label={isEditMode ? 'Save Changes' : 'Add Lecture'}
              >
                {isEditMode ? 'Save Changes' : 'Add Lecture'}
              </button>
            </span>
          </Tooltip>
        )}
      </div>
      <div
        className="ai-loader"
        style={{
          opacity: isMapping ? 1 : 0,
          pointerEvents: isMapping ? 'auto' : 'none',
        }}
      >
        <ThreeOrb isScreensaver={false} />
        <div className="ai-label">Calculating content to assign...</div>
      </div>
      <CustomModal
        open={showClearConfirmation}
        onClose={() => setShowClearConfirmation(false)}
        name="clear-content"
        glass={true}
        blurs={true}
        blurType="error"
      >
        <div className="modal_header">
          <h2>Clear Content</h2>
        </div>
        <div className="modal_content">
          <p>
            Are you sure you want to clear all mapped content? This action
            cannot be undone.
          </p>
        </div>
        <div className="modal_actions">
          <button
            className="button button--secondary"
            onClick={() => {
              setShowClearConfirmation(false);
              hapticsImpactLight();
            }}
            tabIndex={0}
            aria-label="Cancel"
          >
            Cancel
          </button>
          <button
            className="button button--error"
            onClick={handleConfirmClear}
            tabIndex={0}
            aria-label="Clear Content"
          >
            Clear Content
          </button>
        </div>
      </CustomModal>
    </CustomModal>
  );
};

export default LectureEditorModal;
